import React, { lazy, Suspense, useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { MemoryRouter } from 'react-router-dom';
import { TooltipProvider } from '@/shared/components/ui/tooltip';
import App from './App';
import { IS_TAURI } from './lib/platform';
import { CustomerHistoryPage } from './pages/CustomerHistoryPage';
import { ReviewThreadPage } from './pages/ReviewThreadPage';
import { getEnvironmentConfigs } from './lib/tauri';
import { api } from './lib/api';
import './styles/global.css';
import { installLogger } from './lib/logger';

// Side-effect imports: register ECharts modules and the Stream themes
// before any chart component mounts. Without these, ReactECharts is given
// theme names (`stream-dark`/`stream-light`) that it has never seen, so it
// silently falls back to ECharts defaults — including the wrong-way-round
// legend `inactiveColor` (#ccc, brighter than dark-mode body text). See
// `apps/stream/src/lib/charts/theme.ts` for the actual theme definitions.
import './lib/charts/register';
import './lib/charts/theme';

// Install logger before React renders so early errors are captured
installLogger();

// ── Session Viewer mode detection ────────────────────────────────────────────
//
// When `AgentSessionModal` opens a pop-out window it passes `?sv=1` in the
// URL (along with sessionId, agentId, consumerId, env). The new WebviewWindow
// loads the same `index.html`, and this check at module init switches the
// render tree to a lean SessionWindowApp instead of the full Stream bootstrap.
//
// This avoids needing a second Vite entry point (which Vite 5 + Rollup 4
// silently drops when the HTML plugin only processes one root entry).

const _params = new URLSearchParams(window.location.search)
// All three pop-out bootstraps are desktop-only, and `&&` with the build-time
// flag means the browser bundle folds each to `false` and drops the branch.
//
// The session viewer is desktop-only because it inspects Claude agent
// transcripts — an engineering surface — and its page pulls in the
// out-of-scope `features/sessions` module (Task 4093).
//
// `?ch=1` / `?rt=1` were left ungated by 4093 on the expectation that 4094
// would make them load in a browser window. **It did not, and they cannot.**
// Every bootstrap below calls `getEnvironmentConfigs()`, which is a Tauri
// command that throws by design in the browser build — a document opened at
// `index.html?ch=1` would render "Loading…", then the error pane, forever.
// Task 4094 renders these two pages as in-page panels inside the live,
// already-authenticated app instead (see
// `features/queue/components/PopoutPanel`), so nothing in the browser build
// ever navigates to these URLs. Gating them keeps the browser bundle from
// carrying a path that could only fail.
const IS_SESSION_VIEWER   = IS_TAURI && _params.has('sv')
const IS_CUSTOMER_HISTORY = IS_TAURI && _params.has('ch')
const IS_REVIEW_THREAD    = IS_TAURI && _params.has('rt')

// Parse session viewer params once — these never change for the window lifetime.
const SV_CONSUMER_ID  = _params.get('consumerId') ?? ''
const SV_ENV          = (_params.get('env') ?? 'prod') as 'prod' | 'dev'
const SV_SESSION_ID   = _params.get('sessionId') ?? ''
const SV_AGENT_ID     = _params.get('agentId') ?? null
const SV_USER_ID      = _params.get('userId') ?? null
const SV_TITLE        = _params.get('title') ?? null

// Parse customer history viewer params once.
const CH_CONSUMER_ID    = _params.get('consumerId') ?? ''
const CH_ENV            = (_params.get('env') ?? 'prod') as 'prod' | 'dev'
const CH_CUSTOMER_EMAIL = _params.get('customerEmail') ?? null
const CH_CUSTOMER_ID    = _params.get('customerId') ?? null
const CH_CUSTOMER_NAME  = _params.get('customerName') ?? null

// Parse review thread viewer params once.
const RT_CONSUMER_ID   = _params.get('consumerId') ?? ''
const RT_ENV           = (_params.get('env') ?? 'prod') as 'prod' | 'dev'
const RT_INTERACTION_ID = _params.get('interactionId') ?? ''
const RT_REVIEWER_NAME = _params.get('reviewerName') ?? null
const RT_APP_NAME      = _params.get('appName') ?? null

// ── Session viewer bootstrap ─────────────────────────────────────────────────

/**
 * Minimal bootstrap for the session viewer pop-out window.
 *
 * Fetches environment configs for the operator via the Rust layer, configures
 * the API client, then renders `SessionWindowPage` (full-screen portrait
 * transcript viewer, read-only, live by default).
 */
const SessionWindowPage = IS_TAURI
  ? lazy(() =>
      import('./pages/SessionWindowPage').then((m) => ({
        default: m.SessionWindowPage,
      })),
    )
  : null

function SessionWindowApp() {
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!SV_CONSUMER_ID || !SV_SESSION_ID) {
      setError('Missing required parameters (consumerId or sessionId).')
      return
    }

    getEnvironmentConfigs(SV_CONSUMER_ID)
      .then((configs) => {
        let resolvedEnv = SV_ENV
        let config = configs[resolvedEnv]
        if (config.unavailable) {
          resolvedEnv = resolvedEnv === 'prod' ? 'dev' : 'prod'
          config = configs[resolvedEnv]
          if (config.unavailable) {
            setError('No available environment configuration for this operator.')
            return
          }
        }
        api.configure(config.api_key, config.gateway_url)
        setReady(true)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '12px', padding: '32px' }}>
        <p style={{ color: 'var(--error)', fontSize: '14px', margin: 0, textAlign: 'center' }}>{error}</p>
        <p style={{ color: 'var(--text-muted)', fontSize: '12px', margin: 0 }}>Session: {SV_SESSION_ID || '(none)'}</p>
      </div>
    )
  }

  if (!ready) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px', margin: 0 }}>Loading…</p>
      </div>
    )
  }

  // Unreachable in the browser build: IS_SESSION_VIEWER is false there, so this
  // component never mounts and the lazy page is never referenced.
  if (!SessionWindowPage) return null

  return (
    <Suspense fallback={null}>
      <SessionWindowPage
        sessionId={SV_SESSION_ID}
        agentId={SV_AGENT_ID}
        userId={SV_USER_ID}
        title={SV_TITLE}
      />
    </Suspense>
  )
}

// ── Customer history bootstrap ────────────────────────────────────────────────

/**
 * Minimal bootstrap for the customer history pop-out window.
 *
 * Fetches environment configs for the operator via the Rust layer, configures
 * the API client, then renders `CustomerHistoryPage` (full-screen interaction
 * history viewer showing all emails for the customer).
 */
function CustomerHistoryApp() {
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!CH_CONSUMER_ID) {
      setError('Missing required parameter (consumerId).')
      return
    }

    getEnvironmentConfigs(CH_CONSUMER_ID)
      .then((configs) => {
        let resolvedEnv = CH_ENV
        let config = configs[resolvedEnv]
        if (config.unavailable) {
          resolvedEnv = resolvedEnv === 'prod' ? 'dev' : 'prod'
          config = configs[resolvedEnv]
          if (config.unavailable) {
            setError('No available environment configuration for this operator.')
            return
          }
        }
        api.configure(config.api_key, config.gateway_url)
        setReady(true)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '12px', padding: '32px' }}>
        <p style={{ color: 'var(--error)', fontSize: '14px', margin: 0, textAlign: 'center' }}>{error}</p>
      </div>
    )
  }

  if (!ready) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px', margin: 0 }}>Loading…</p>
      </div>
    )
  }

  return (
    <CustomerHistoryPage
      customerEmail={CH_CUSTOMER_EMAIL}
      customerId={CH_CUSTOMER_ID}
      customerName={CH_CUSTOMER_NAME}
    />
  )
}

// ── Review thread bootstrap ───────────────────────────────────────────────────

/**
 * Minimal bootstrap for the review thread pop-out window.
 *
 * Fetches environment configs for the operator via the Rust layer, configures
 * the API client, then renders `ReviewThreadPage` (full-screen review-thread
 * viewer showing the original review, the developer reply, and any customer
 * follow-up — keyed by interaction id instead of email).
 */
function ReviewThreadApp() {
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!RT_CONSUMER_ID || !RT_INTERACTION_ID) {
      setError('Missing required parameters (consumerId or interactionId).')
      return
    }

    getEnvironmentConfigs(RT_CONSUMER_ID)
      .then((configs) => {
        let resolvedEnv = RT_ENV
        let config = configs[resolvedEnv]
        if (config.unavailable) {
          resolvedEnv = resolvedEnv === 'prod' ? 'dev' : 'prod'
          config = configs[resolvedEnv]
          if (config.unavailable) {
            setError('No available environment configuration for this operator.')
            return
          }
        }
        api.configure(config.api_key, config.gateway_url)
        setReady(true)
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err))
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: '12px', padding: '32px' }}>
        <p style={{ color: 'var(--error)', fontSize: '14px', margin: 0, textAlign: 'center' }}>{error}</p>
      </div>
    )
  }

  if (!ready) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: '14px', margin: 0 }}>Loading…</p>
      </div>
    )
  }

  return (
    <ReviewThreadPage
      interactionId={RT_INTERACTION_ID}
      reviewerName={RT_REVIEWER_NAME}
      appName={RT_APP_NAME}
    />
  )
}

// ── Mount ────────────────────────────────────────────────────────────────────

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {IS_SESSION_VIEWER ? (
      /*
       * Session viewer mode: a separate OS-level WebviewWindow loading
       * index.html?sv=1&sessionId=... — render only the transcript viewer,
       * no auth/operator/navigation chrome.
       */
      <MemoryRouter>
        <TooltipProvider>
          <SessionWindowApp />
        </TooltipProvider>
      </MemoryRouter>
    ) : IS_CUSTOMER_HISTORY ? (
      /*
       * Customer history mode: a separate OS-level WebviewWindow loading
       * index.html?ch=1&customerEmail=... — render only the interaction
       * history viewer, no auth/operator/navigation chrome.
       */
      <MemoryRouter>
        <TooltipProvider>
          <CustomerHistoryApp />
        </TooltipProvider>
      </MemoryRouter>
    ) : IS_REVIEW_THREAD ? (
      /*
       * Review thread mode: a separate OS-level WebviewWindow loading
       * index.html?rt=1&interactionId=... — render only the review-thread
       * viewer, no auth/operator/navigation chrome.
       */
      <MemoryRouter>
        <TooltipProvider>
          <ReviewThreadApp />
        </TooltipProvider>
      </MemoryRouter>
    ) : (
      <App />
    )}
  </React.StrictMode>
);
